/**
 * Leads — every lead in the workspace, and the one worth acting on today.
 *
 * Visual Reference: `design/screens/leads-list.html`, five states — default,
 * loading, empty, filtered, error.
 *
 * PRD §1.9, the claim this screen exists to make good on: "**the Verdict.**
 * Every screen leads with a plain-language sentence, not a number… This holds
 * on… the leads list". So the row reads "Worth calling today · funded, board
 * approved" and the score sits at the end in tabular numerals, not the front.
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DRAW, and why:
 *
 *   · **"Assign to a rep"** — DECISION-017: not built at M1, no `assigned_to`
 *     column, and "an assignee nobody is notified about and no view filters by
 *     is half a feature". The mockup's bulk bar draws it; this does not.
 *   · **The "Had a call" filter** — M5, and DECISION-016 keeps a
 *     later-milestone control off an M1 screen rather than rendering it
 *     disabled forever.
 *   · **Selection checkboxes, the bulk bar and the saved-view picker** — Story
 *     8.3 owns all three, and `Export CSV` is Story 8.6. The mockup draws them;
 *     they arrive with the stories that make them do something.
 *   · **The over-cap row's blurred score.** The export renders `64` behind
 *     `filter: blur(2.5px)`. DECISION-015: "never send what the UI hides" — the
 *     blur is presentation and the omission is the control, so the API sends no
 *     score for that row and this renders no score cell at all. That is the
 *     single largest deliberate deviation from the mockup on this screen and it
 *     is the point of PRD §4.5's claim, not a shortfall against it.
 *
 * Story 8.2.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiGet, apiPost, apiDelete, ApiFailure } from '../lib/api'
import { leadName } from '../lib/lead-name'
import { BILLING_PATH } from '../shell/settings-nav'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Pill } from '../ui/Pill'
import { Skeleton } from '../ui/Skeleton'

/** A row, as `GET /v1/w/{wid}/leads` serialises it through the seam. */
export type Lead = {
  id: string
  contactName?: string | null
  contactCompany?: string | null
  /** Absent over the cap — DECISION-015. Never null, never blurred. */
  score?: number
  band?: string
  bandLabel: string | null
  tone: 'hot' | 'review' | 'nurture' | null
  verdictSentence?: string | null
  verdictReason?: string | null
  status: string
  overCap: boolean
  createdAt: number
}

type View = { leads: Lead[]; nextCursor?: string; total: number }

/** A saved filter set. `userId: null` means the whole workspace has it. */
type SavedView = {
  id: string
  name: string
  userId: string | null
  filters: { bands?: string[]; search?: string }
  createdAt: number
}

/** The band ladder, in the order the filter pills read left to right. */
const TONES = ['hot', 'review', 'nurture'] as const
type Tone = (typeof TONES)[number]

/**
 * The avatar's colour, measured off the export rather than derived.
 *
 * NOT the band tone's own colour — a Hot lead's avatar is `--mdx-brand`, not
 * `--mdx-hot`. The mockup uses brand / slate / nurture across the three bands,
 * which keeps the green for the pill that means qualified and stops a list of
 * twelve avatars reading as a traffic light.
 *
 * Exported, with the pill tones and the initials below, for Story 22.3's review
 * queue, whose rows are leads drawn the same way.
 */
export const AVATAR: Record<Tone, string> = {
  hot: 'bg-mdx-brand',
  review: 'bg-mdx-slate',
  nurture: 'bg-mdx-nurture',
}

export const PILL_TONE: Record<Tone, 'hot' | 'review' | 'neutral'> = {
  hot: 'hot',
  review: 'review',
  nurture: 'neutral',
}

/** "SC" from "Sarah Chen"; "—" from a name a Viewer may not see. */
export function initials(name: string | null | undefined): string {
  if (!name) return '·'
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '·'
}

/**
 * The date separator's words.
 *
 * Today and Yesterday by name because that is what the mockup draws and what a
 * person scanning for "did anything come in this morning" is actually asking.
 * Anything older gets its date: "Yesterday" three days running would be a lie
 * told to save a word.
 */
function dayLabel(at: number, now: number): string {
  const start = (t: number) => {
    const d = new Date(t)
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }
  const days = Math.round((start(now) - start(at)) / 86_400_000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
}

type Group = { label: string; leads: Lead[] }

function groupByDay(leads: Lead[], now: number): Group[] {
  const out: Group[] = []
  for (const lead of leads) {
    const label = dayLabel(lead.createdAt, now)
    const last = out[out.length - 1]
    if (last && last.label === label) last.leads.push(lead)
    else out.push({ label, leads: [lead] })
  }
  return out
}

export function Leads() {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  const [view, setView] = useState<View | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [bands, setBands] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [views, setViews] = useState<SavedView[]>([])
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [naming, setNaming] = useState(false)
  const [viewName, setViewName] = useState('')
  const [viewShared, setViewShared] = useState(false)

  /** The filters, as the query string the API reads. */
  const query = useMemo(() => {
    const p = new URLSearchParams()
    for (const b of bands) p.append('band', b)
    if (search.trim()) p.set('q', search.trim())
    return p.toString()
  }, [bands, search])

  const filtered = bands.length > 0 || search.trim().length > 0

  const load = useCallback(async () => {
    if (!workspaceId) return
    setError(null)
    setView(null)
    try {
      setView(await apiGet<View>(`/v1/w/${workspaceId}/leads${query ? `?${query}` : ''}`))
    } catch (e) {
      // The message NAMES THE FIX and is rendered as it arrives (ARCH §4.3).
      setError(
        e instanceof ApiFailure
          ? e.message
          : 'The list came back empty-handed. Your leads are still captured and still scored — load the list again.',
      )
    }
  }, [workspaceId, query])

  const loadViews = useCallback(async () => {
    if (!workspaceId) return
    try {
      const res = await apiGet<{ views: SavedView[] }>(`/v1/w/${workspaceId}/saved-views`)
      setViews(res.views)
    } catch {
      // A sidebar of bookmarks failing to load must not take the leads with it.
      setViews([])
    }
  }, [workspaceId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void loadViews()
  }, [loadViews])

  /**
   * The selection is cleared whenever the LIST changes.
   *
   * Otherwise a filter change leaves ids selected for rows nobody can see any
   * more, and "2 selected" would act on leads that are no longer on screen —
   * which is the shape of a bulk action somebody did not mean to run.
   */
  useEffect(() => {
    setSelected(new Set())
  }, [query])

  const runBulk = async (action: 'mark_contacted' | 'archive') => {
    if (!workspaceId || selected.size === 0) return
    setBusy(true)
    setError(null)
    try {
      await apiPost(`/v1/w/${workspaceId}/leads/bulk`, { action, ids: [...selected] })
      setSelected(new Set())
      await load()
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "That didn't go through. Nothing changed — try it again.",
      )
    } finally {
      setBusy(false)
    }
  }

  /**
   * DECISION-081. The name is typed INTO THE PAGE, not into a browser dialog.
   *
   * This was `window.prompt` until Q-056 was answered: unstyleable, blocking,
   * and it would freeze Playwright the moment anything tested the save path.
   * The shared/personal choice is exposed here too, because
   * `saved_views.user_id` is the whole of the feature's access model and a
   * default nobody can see is not a choice.
   */
  const saveView = async () => {
    if (!workspaceId) return
    const name = viewName.trim()
    if (!name) return
    try {
      await apiPost(`/v1/w/${workspaceId}/saved-views`, {
        name,
        shared: viewShared,
        filters: { ...(bands.length ? { bands } : {}), ...(search.trim() ? { search: search.trim() } : {}) },
      })
      setNaming(false)
      setViewName('')
      setViewShared(false)
      await loadViews()
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : "That view couldn't be saved.")
    }
  }

  const removeView = async (id: string) => {
    if (!workspaceId) return
    try {
      await apiDelete(`/v1/w/${workspaceId}/saved-views?id=${encodeURIComponent(id)}`)
      await loadViews()
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : "That view couldn't be deleted.")
    }
  }

  const applyView = (view: SavedView) => {
    setBands(view.filters.bands ?? [])
    setSearch(view.filters.search ?? '')
  }

  const toggleLead = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  /**
   * Export — Story 8.6.
   *
   * The response is a TICKET and not a file: `POST` builds the CSV into R2 and
   * returns a short-lived URL, which the browser is then sent to. Never inline,
   * so the download is resumable and can be handed to a colleague inside its
   * fifteen minutes.
   *
   * Rendered for everyone; the server refuses without `can_export` and the
   * message says so. PRD §2.3 separates the permission from the role
   * deliberately — "some Reviewers should read without extracting" — and a
   * button that vanishes teaches nobody why.
   */
  const runExport = async () => {
    if (!workspaceId || exporting) return
    setExporting(true)
    setError(null)
    try {
      const ticket = await apiPost<{ url: string }>(`/v1/w/${workspaceId}/leads/export`, {
        filters: {
          ...(bands.length ? { bands } : {}),
          ...(search.trim() ? { search: search.trim() } : {}),
        },
      })
      window.location.assign(ticket.url)
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : "That export couldn't be built. Try again.")
    } finally {
      setExporting(false)
    }
  }

  const clearFilters = () => {
    setBands([])
    setSearch('')
  }

  const toggleBand = (tone: Tone) =>
    setBands((current) =>
      current.includes(tone) ? current.filter((b) => b !== tone) : [...current, tone],
    )

  const now = Date.now()
  const groups = view ? groupByDay(view.leads, now) : []

  return (
    <div className="flex flex-col gap-mdx-16">
      <header className="flex flex-wrap items-end justify-between gap-mdx-16">
        <div className="flex flex-col gap-mdx-4">
        <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
          {workspace?.name ?? 'This workspace'} · all funnels
        </span>
        <h1 className="m-0 text-mdx-25 font-mdx-bold leading-mdx-tight tracking-mdx-h1 text-mdx-ink">
          Leads{' '}
          {view ? (
            <span className="font-mdx-medium text-mdx-slate tabular-nums">
              {view.leads.length} of {view.total.toLocaleString()}
            </span>
          ) : null}
        </h1>
        </div>
        {view && view.total > 0 ? (
          <Button variant="secondary" onClick={() => void runExport()} disabled={exporting}>
            Export CSV
          </Button>
        ) : null}
      </header>

      {/* The filter rail. Band, then search — the two the API can serve without
          a scan, in the mockup's own order. Status, date range and funnel are
          drawn there too and are wired by Story 8.3 with the saved views that
          persist them; a control that filtered nothing would be worse than one
          that is not there yet. */}
      <div className="flex flex-wrap items-center gap-mdx-12 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-12 shadow-mdx-card">
        <span className="text-mdx-13 font-mdx-semibold text-mdx-slate">Band</span>
        <div className="flex flex-wrap items-center gap-mdx-6">
          {TONES.map((tone) => {
            const on = bands.includes(tone)
            return (
              <button
                key={tone}
                type="button"
                aria-pressed={on}
                onClick={() => toggleBand(tone)}
                className={[
                  'cursor-pointer rounded-mdx-pill px-mdx-12 py-mdx-5',
                  'text-mdx-13 font-mdx-semibold capitalize transition-colors ease-mdx-swift',
                  on
                    ? 'border border-mdx-brand bg-mdx-brand-tint text-mdx-brand-deep'
                    : 'border border-mdx-line bg-mdx-white text-mdx-slate hover:bg-mdx-ground',
                ].join(' ')}
              >
                {tone}
              </button>
            )
          })}
        </div>

        {/* Saved views — Story 8.3.
            The export ALSO lists them in the left sidebar, under a "Saved
            views" nav section with "Review queue" above it. That sidebar is not
            the one that shipped: DECISION-018 fixed the eight nav destinations
            and the shell was built to it. Putting them here keeps them beside
            the filters they ARE, and does not reopen a settled decision. */}
        {views.length > 0 ? (
          <div className="flex flex-wrap items-center gap-mdx-6">
            <span className="text-mdx-13 font-mdx-semibold text-mdx-slate">Saved view</span>
            {views.map((view) => (
              <span key={view.id} className="inline-flex items-center">
                <button
                  type="button"
                  // Its own label, so it is distinguishable from the × beside
                  // it: both otherwise carry the view's name and a test asking
                  // for one gets both.
                  aria-label={`Apply ${view.name}`}
                  onClick={() => applyView(view)}
                  className="cursor-pointer rounded-l-mdx-control border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-5 text-mdx-13 font-mdx-semibold text-mdx-ink transition-colors ease-mdx-swift hover:bg-mdx-ground"
                >
                  {view.name}
                  {view.userId === null ? (
                    <span className="text-mdx-slate"> · shared</span>
                  ) : null}
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${view.name}`}
                  onClick={() => void removeView(view.id)}
                  className="cursor-pointer rounded-r-mdx-control border border-l-0 border-mdx-line bg-mdx-white px-mdx-8 py-mdx-5 text-mdx-13 text-mdx-slate transition-colors ease-mdx-swift hover:bg-mdx-ground"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}

        {filtered && !naming ? (
          <Button variant="secondary" onClick={() => setNaming(true)}>
            Save this view
          </Button>
        ) : null}

        {naming ? (
          <span className="flex flex-wrap items-center gap-mdx-8">
            <input
              type="text"
              value={viewName}
              autoFocus
              maxLength={60}
              placeholder="Name this view"
              onChange={(e) => setViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void saveView()
                if (e.key === 'Escape') setNaming(false)
              }}
              aria-label="Name this view"
              className="h-mdx-input w-mdx-row-identity-sm rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-12 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand"
            />
            <label className="flex cursor-pointer items-center gap-mdx-6 text-mdx-13 text-mdx-slate">
              <input
                type="checkbox"
                checked={viewShared}
                onChange={(e) => setViewShared(e.target.checked)}
                className="size-mdx-18 cursor-pointer accent-mdx-brand"
              />
              Share with the workspace
            </label>
            <Button onClick={() => void saveView()} disabled={!viewName.trim()}>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setNaming(false)}>
              Cancel
            </Button>
          </span>
        ) : null}

        <label className="ml-auto flex items-center gap-mdx-8">
          <span className="text-mdx-13 font-mdx-semibold text-mdx-slate">Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, company or email"
            className="h-mdx-input w-mdx-row-control rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 text-mdx-15 text-mdx-ink outline-none transition-colors ease-mdx-swift focus:border-mdx-brand"
          />
        </label>
      </div>

      <div className="flex flex-col gap-mdx-8 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-16 shadow-mdx-card">
        {error ? (
          <div className="flex flex-col items-center gap-mdx-12 px-mdx-24 py-mdx-48 text-center">
            <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">
              We couldn&rsquo;t load your leads
            </span>
            <span className="max-w-mdx-measure text-mdx-15 text-mdx-slate">{error}</span>
            <Button onClick={() => void load()}>Try again</Button>
          </div>
        ) : view === null ? (
          // The layout is HELD, not replaced by a spinner — DESIGN-SYSTEM §2.
          <div className="flex flex-col gap-mdx-2" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-mdx-14 px-mdx-16 py-mdx-14">
                <Skeleton className="size-mdx-avatar-sm rounded-mdx-pill" />
                <Skeleton className="h-mdx-14 w-mdx-row-identity-sm rounded-mdx-sm" />
                <Skeleton className="h-mdx-14 flex-1 rounded-mdx-sm" />
              </div>
            ))}
          </div>
        ) : view.leads.length === 0 && filtered ? (
          <div className="flex flex-col items-center gap-mdx-12 px-mdx-24 py-mdx-48 text-center">
            <span className="size-mdx-avatar-lg rounded-mdx-control bg-mdx-line" aria-hidden="true" />
            <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">
              No leads match these filters
            </span>
            <Button variant="secondary" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        ) : view.leads.length === 0 ? (
          <div className="flex flex-col items-center gap-mdx-12 px-mdx-24 py-mdx-48 text-center">
            <span className="size-mdx-avatar-lg rounded-mdx-control bg-mdx-line" aria-hidden="true" />
            <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">
              No leads yet
            </span>
            <span className="max-w-mdx-measure text-mdx-15 text-mdx-slate">
              Put your funnel on the page your buyers already visit. The first answers land here
              within minutes.
            </span>
            {/* The snippet itself lives on the funnel that owns it — Story 6.7's
                embed panel — so this points there rather than printing a script
                tag this screen cannot know the funnel id for. */}
            <Button onClick={() => window.location.assign('/funnels')}>Go to your funnels</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-mdx-2">
            {groups.map((group) => (
              <div key={group.label} className="flex flex-col gap-mdx-2">
                <div className="flex items-center gap-mdx-12 px-mdx-16 pt-mdx-8 pb-mdx-4">
                  <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
                    {group.label}
                  </span>
                  <span className="h-px flex-1 bg-mdx-line-soft" />
                  <span className="text-mdx-13 text-mdx-slate tabular-nums">
                    {group.leads.length} {group.leads.length === 1 ? 'lead' : 'leads'}
                  </span>
                </div>

                {group.leads.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    selected={selected.has(lead.id)}
                    onToggle={toggleLead}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/*
        The selection bar — Story 8.3, sticky at the bottom on `--mdx-ink`.

        THREE BUTTONS, NOT FOUR. The export draws "Mark as called", "Assign to a
        rep", "Export selection" and "Clear". DECISION-017 does not build
        assignment at M1 — "an assignee nobody is notified about and no view
        filters by is half a feature" — and export is Story 8.6 AND a separate
        permission, which is why `POST /leads/bulk` refuses it as an action
        rather than accepting it.

        `Archive` takes the slot assignment vacates. The API contract names it
        beside `mark_contacted` and this story's AC requires it, so the
        alternative was a bulk action with no way to run it. Same bar, same
        outline treatment; listed as a deviation.
      */}
      {selected.size > 0 ? (
        <div className="sticky bottom-mdx-12 z-10 flex max-w-full flex-wrap items-center gap-mdx-12 self-center rounded-mdx-control bg-mdx-ink py-mdx-10 pl-mdx-16 pr-mdx-12 text-mdx-white shadow-mdx-modal">
          <span className="whitespace-nowrap text-mdx-15 font-mdx-semibold tabular-nums">
            {selected.size} selected
          </span>
          <span className="flex flex-wrap gap-mdx-8">
            <button
              type="button"
              disabled={busy}
              onClick={() => void runBulk('mark_contacted')}
              className="cursor-pointer rounded-mdx-control-sm border-0 bg-mdx-brand px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-white transition-colors ease-mdx-swift disabled:cursor-not-allowed disabled:opacity-mdx-recessed"
            >
              Mark as called
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void runBulk('archive')}
              className="cursor-pointer rounded-mdx-control-sm border border-mdx-line bg-transparent px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-white transition-colors ease-mdx-swift disabled:cursor-not-allowed disabled:opacity-mdx-recessed"
            >
              Archive
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="cursor-pointer rounded-mdx-control-sm border-0 bg-transparent px-mdx-12 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-white underline-offset-2 hover:underline"
            >
              Clear
            </button>
          </span>
        </div>
      ) : null}
    </div>
  )
}

/**
 * One row.
 *
 * THE COLUMNS CARRY REAL FLEX BASES, not `flex-1`. The export gives the name
 * `flex: 0 1 124px` and the verdict `flex: 1 1 260px`, and those bases are what
 * make the row WRAP at 375 rather than squeeze: 130 + 210 does not fit a
 * 375-wide card, so the verdict takes its own line and reads normally. Built
 * with `flex-1` first — basis 0, no breakpoint — and the 375 capture showed
 * "Worth calling today" broken across three lines beside a fixed-width name.
 * The nearest tokens to the export's 124 and 260 are `--mdx-row-identity-sm`
 * (130) and `--mdx-row-control` (210); both are listed as deviations.
 */
export function LeadRow({
  lead,
  selected,
  onToggle,
}: {
  lead: Lead
  /** Both optional: a read-only surface reuses the row without a selection. */
  selected?: boolean
  onToggle?: (id: string) => void
}) {
  const tone = lead.tone
  return (
    <div
      className={[
        'flex flex-wrap items-center gap-x-mdx-14 gap-y-mdx-8 rounded-mdx-control px-mdx-16 py-mdx-14',
        // The export tints a SELECTED row `--mdx-brand-tint` and an over-cap one
        // `--mdx-ground`. Selection wins when both are true: the operator just
        // clicked it, and the tint is the feedback for that click.
        selected ? 'bg-mdx-brand-tint' : lead.overCap ? 'bg-mdx-ground' : 'bg-transparent',
      ].join(' ')}
    >
      {onToggle ? (
        <input
          type="checkbox"
          checked={selected ?? false}
          onChange={() => onToggle(lead.id)}
          aria-label={`Select ${lead.contactName ?? 'this lead'}`}
          className="size-mdx-18 flex-none cursor-pointer accent-mdx-brand"
        />
      ) : null}
      <span
        aria-hidden="true"
        className={[
          'flex size-mdx-avatar-sm flex-none items-center justify-center rounded-mdx-pill',
          'text-mdx-12 font-mdx-bold text-mdx-white',
          tone ? AVATAR[tone] : 'bg-mdx-nurture',
          // The plan is what is holding this row back, not its state
          // (DECISION-075 named this step for exactly these two surfaces).
          lead.overCap ? 'opacity-mdx-withheld' : '',
        ].join(' ')}
      >
        {initials(lead.contactName)}
      </span>

      {/* The name opens the drawer. An over-cap lead has no drawer to open —
          `GET /leads/{lid}` 404s over the cap by contract — so it is not a link,
          and the row's own "Upgrade to see details" is the affordance instead. */}
      {lead.overCap ? (
        <span className="basis-mdx-row-identity-sm shrink-0 font-mdx-semibold text-mdx-slate">
          {leadName(lead.contactName)}
        </span>
      ) : (
        <a
          href={`/leads/${lead.id}`}
          className="basis-mdx-row-identity-sm shrink-0 font-mdx-semibold text-mdx-ink no-underline hover:underline"
        >
          {leadName(lead.contactName)}
        </a>
      )}

      {lead.overCap ? (
        <span className="flex min-w-0 grow basis-mdx-row-control flex-wrap items-center gap-mdx-10">
          <button
            type="button"
            onClick={() => window.location.assign(BILLING_PATH)}
            className="cursor-pointer whitespace-nowrap rounded-mdx-control-sm border-0 bg-mdx-brand-tint px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-brand-deep transition-[filter] ease-mdx-swift hover:brightness-[.96]"
          >
            Upgrade to see details
          </button>
          <span className="text-mdx-13 text-mdx-slate">
            Captured and scored, beyond your plan cap
          </span>
        </span>
      ) : (
        <span className="min-w-0 grow basis-mdx-row-control text-mdx-15">
          <span className="font-mdx-semibold text-mdx-ink">{lead.verdictSentence ?? '—'}</span>
          {lead.verdictReason ? (
            <span className="text-mdx-slate"> · {lead.verdictReason}</span>
          ) : null}
        </span>
      )}

      {/* Over the cap the pill says `Hidden` and says it PLAINLY. The export
          blurs it; DECISION-015 makes the omission the control, and a blurred
          word invites a screenshot and a filter-removal in devtools. */}
      <Pill tone={lead.overCap ? 'neutral' : tone ? PILL_TONE[tone] : 'neutral'} dot>
        {lead.overCap ? 'Hidden' : (lead.bandLabel ?? 'Unbanded')}
      </Pill>

      {/*
        The column is RESERVED even when there is no number for it.

        The response carries no score over the cap and this renders none — that
        part is DECISION-015 and is not negotiable. But the WIDTH is layout, not
        data: dropping the cell pulled every over-cap row's band pill 45px right
        of the ones above it, which the 1440 capture showed plainly. Holding the
        column costs nothing and says nothing.
      */}
      <span
        aria-hidden={lead.score === undefined}
        className="w-mdx-icon-button flex-none text-right font-mdx-bold text-mdx-slate tabular-nums"
      >
        {lead.score === undefined ? '' : lead.score}
      </span>
    </div>
  )
}
